import { useEffect, useState } from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Box,
  Stack,
  Chip,
  Card,
  Avatar,
  Typography,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { 
  FolderOpen as FolderOpenIcon,
  Clear as ClearIcon,
  InsertDriveFile as FileIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import type { MediaFile, MediaImage } from '../../models/media';
import { useMediaDialog } from './MediaDialogProvider';
import { mediaFileApi } from '../../services/api';

type MediaValue = number | string | MediaFile | MediaImage | null;
type ValueType = 'id' | 'url' | 'object' | null;

// Helper type to infer the expected type from the onChange function
// type InferValueType<T> = T extends (value: infer U) => void ? U : never;

interface MediaSelectorProps<T extends MediaValue> {
  value?: T;
  onChange: (value: T) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  fileTypeFilter?: 'all' | 'images' | 'documents';
  previewSize?: 'small' | 'medium' | 'large';
  showPreview?: boolean;
}

const MediaSelector = <T extends MediaValue>({
  value = null as T,
  onChange,
  label = 'File URL',
  required = false,
  disabled = false,
  placeholder = 'Enter URL or select file from media library',
  fileTypeFilter = 'all',
  previewSize = 'small',
  showPreview = true,
}: MediaSelectorProps<T>) => {
  const { openMediaDialog } = useMediaDialog();
  const [loading, setLoading] = useState(false);
  const [fileDetails, setFileDetails] = useState<MediaFile | MediaImage | null>(null);
  const [displayValue, setDisplayValue] = useState<string>('');
  const [valueType, setValueType] = useState<ValueType>(null);

  // Infer the expected type from the onChange function
  // type ExpectedType = InferValueType<typeof onChange>;
  
  // Helper function to convert file to expected type
  const convertToExpectedType = (file: MediaFile | MediaImage): T => {
    if (valueType === 'id') {
      return file.id as T;
    }
    if (valueType === 'url') {
      return file.file as T;
    }
    if (valueType === 'object') {
      return file as T;
    }
    
    // If valueType is null, infer from parent type
    // if (typeof value === 'number' || (value === null && typeof onChange === 'function' && 
    //     (onChange as (value: number) => void).toString().includes('number'))) {
    //   return file.id as T;
    // }
    // if (typeof value === 'string' || (value === null && typeof onChange === 'function' && 
    //     (onChange as (value: string) => void).toString().includes('string'))) {
    //   return file.file as T;
    // }
    // Default to full object
    return file as T;
  };
  
  useEffect(() => {
    const fetchFileDetails = async () => {
      if (value === null) {
        setFileDetails(null);
        setDisplayValue('');
        setValueType(null);
        return;
      }

      // If value is a string (URL), use it directly
      if (typeof value === 'string') {
        setFileDetails(null);
        setDisplayValue(value);
        setValueType('url');
        return;
      }

      // If value is a number (ID), fetch details
      if (typeof value === 'number') {
        setValueType('id');
        if(!value) {
          setFileDetails(null);
          setDisplayValue('');
          return;
        }
        try {
          setLoading(true);
          const response = await mediaFileApi.getById(value);
          setFileDetails(response.data);
          setDisplayValue(response.data.file);
          
        } catch (error) {
          console.error('Error fetching file details:', error);
          setFileDetails(null);
          setDisplayValue('');
        } finally {
          setLoading(false);
        }
        return;
      }

      // If value is an object (MediaFile or MediaImage)
      if (typeof value === 'object') {
        setFileDetails(value);
        setDisplayValue(value.file);
        setValueType('object');
        return;
      }
    };

    fetchFileDetails();
  }, [value]);
  
  const handleOpenDialog = () => {
    openMediaDialog({
      onSelect: (file: MediaFile | MediaImage) => {
        onChange(convertToExpectedType(file));
      },
      fileTypeFilter,
    });
  };
  
  const handleTextChange = (newValue: string) => {
    if (valueType === 'id' && fileDetails) {
      onChange(fileDetails.id as T);
    } else if (valueType === 'url') {
      onChange(newValue as T);
    } else if (valueType === 'object' && fileDetails) {
      onChange(fileDetails as T);
    } else {
      // If valueType is null, infer from parent type
      if (typeof value === 'number' || (value === null && typeof onChange === 'function' && 
          (onChange as (value: number) => void).toString().includes('number'))) {
        // If parent expects number, we can't handle text input
        return;
      }
      if (typeof value === 'string' || (value === null && typeof onChange === 'function' && 
          (onChange as (value: string) => void).toString().includes('string'))) {
        onChange(newValue as T);
      } else {
        // If parent expects object, we can't handle text input
        return;
      }
    }
  };
  
  // Create a short display name for the selected file
  const getShortFileName = () => {
    if (!displayValue) return '';
    const parts = displayValue.split('/');
    return parts[parts.length - 1];
  };
  
  // Determine if the current value is an image
  const isImage = displayValue && (
    displayValue.toLowerCase().endsWith('.jpg') || 
    displayValue.toLowerCase().endsWith('.jpeg') || 
    displayValue.toLowerCase().endsWith('.png') || 
    displayValue.toLowerCase().endsWith('.gif') || 
    displayValue.toLowerCase().endsWith('.webp') || 
    displayValue.toLowerCase().endsWith('.svg')
  );

  // Get the preview URL, preferring thumbnail if available
  const getPreviewUrl = () => {
    if (fileDetails && 'thumbnail_url' in fileDetails && fileDetails.thumbnail_url?.small) {
      return fileDetails.thumbnail_url.small;
    }
    return displayValue;
  };

  const getPreviewSize = () => {
    switch (previewSize) {
      case 'large':
        return { width: 400, height: 300 };
      case 'medium':
        return { width: 200, height: 150 };
      case 'small':
      default:
        return { width: 40, height: 40 };
    }
  };
  
  const handleClear = () => {
    onChange(null as T);
  };

  const previewDimensions = getPreviewSize();
  
  // Determine if text input should be enabled
  const isTextInputEnabled = valueType === 'url' || 
    (valueType === null && (typeof value === 'string' || 
    (value === null && typeof onChange === 'function' && 
    (onChange as (value: string) => void).toString().includes('string'))));
  
  return (
    <Box sx={{ width: '100%' }}>
      <TextField
        fullWidth
        label={label}
        value={displayValue}
        onChange={(e) => handleTextChange(e.target.value)}
        required={required}
        disabled={disabled || loading || !isTextInputEnabled}
        placeholder={isTextInputEnabled ? placeholder : 'Select from media library'}
        sx={{
          '& .MuiInputBase-input': {
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            maxWidth: 'calc(100% - 40px)'
          }
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Stack direction="row" spacing={1}>
                {loading ? (
                  <CircularProgress size={20} />
                ) : (
                  <>
                    {displayValue && (
                      <Tooltip title="View full size">
                        <IconButton
                          edge="end"
                          onClick={() => window.open(displayValue, '_blank')}
                          disabled={disabled}
                          size="small"
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Select from Media Library">
                      <IconButton 
                        edge="end" 
                        onClick={handleOpenDialog}
                        disabled={disabled}
                        size="small"
                      >
                        <FolderOpenIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Stack>
            </InputAdornment>
          ),
        }}
      />
      
      {/* Show preview if a file is selected */}
      {displayValue && showPreview && (
        <Card variant="outlined" sx={{ mt: 2, p: 1 }}>
          <Stack 
            direction="row" 
            spacing={2} 
            alignItems="center" 
            justifyContent="space-between"
            sx={{ width: '100%' }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              {isImage ? (
                <Box
                  sx={{
                    width: previewDimensions.width,
                    height: previewDimensions.height,
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 1,
                  }}
                >
                  <img
                    src={getPreviewUrl()}
                    alt={getShortFileName()}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </Box>
              ) : (
                <Avatar 
                  variant="rounded" 
                  sx={{ 
                    bgcolor: 'action.selected', 
                    width: previewDimensions.width, 
                    height: previewDimensions.height 
                  }}
                >
                  <FileIcon />
                </Avatar>
              )}
              
              <Stack spacing={0.5}>
                <Chip 
                  label={getShortFileName()}
                  variant="outlined"
                  size="small"
                />
                {isImage && (
                  <Typography variant="caption" color="text.secondary">
                    {previewSize === 'large' ? 'Large Preview' : previewSize === 'medium' ? 'Medium Preview' : 'Small Preview'}
                  </Typography>
                )}
              </Stack>
            </Stack>
            
            <IconButton 
              size="small" 
              onClick={handleClear}
              disabled={disabled}
              title="Clear selection"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Card>
      )}
    </Box>
  );
};

export default MediaSelector;
